import React, { ReactElement, forwardRef } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import DialogActions from '@mui/material/DialogActions'
import { SxProps, useTheme } from '@mui/material/styles'
import DialogContent from '@mui/material/DialogContent'
import CloseSvgIcon from '../../svg/CloseSvglcon'
import DialogTitle from '@mui/material/DialogTitle'
import { Theme } from '@mui/material'
import { useLanguageContext } from '@/contexts/LanguageContext'

export enum actionDialogType {
  REGULAR = 'regular',
  WARNING = 'warning'
}

export interface Props {
  open: boolean
  title?: string
  onConfirm?: () => void
  content?: string | ReactElement
  icon?: React.ReactElement
  confirmLabel?: string
  onCancel?: () => void
  cancelLabel?: string
  onClose?: () => void
  type?: actionDialogType
  isLoading?: boolean
  hasConfirmDisabled?: boolean
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
  customSx?: SxProps<Theme>
  disablePortal?: boolean // The children will be under the DOM hierarchy of the parent component.
}

const FdDialog = (
  {
    open,
    title,
    content,
    onConfirm,
    confirmLabel,
    hasConfirmDisabled = false,
    onCancel,
    cancelLabel,
    onClose,
    isLoading,
    maxWidth,
    fullWidth,
    disablePortal,
    customSx
  }: Props,
  ref: any
) => {
  const { translations } = useLanguageContext()
  confirmLabel = confirmLabel ?? translations.YES
  cancelLabel = cancelLabel ?? translations.NO
  const theme = useTheme()
  const handleConfirmClick = () => {
    if (typeof onConfirm === 'function') {
      onConfirm()
    }
  }
  const handleCancelClick = () => {
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }
  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose()
    }
  }
  return (
    <Dialog
      onClose={() => {
        !isLoading && handleClose()
      }}
      aria-labelledby='dialog-title'
      open={open}
      ref={ref}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disablePortal={disablePortal}
      sx={{
        ...customSx
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.colors.stroke}`,
          p: 3
        }}
      >
        {title || ''}
        {onClose && !isLoading && (
          <IconButton onClick={handleClose}>
            <CloseSvgIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions
        sx={{
          bgcolor: theme.palette.background.default,
          borderTop: `1px solid ${theme.colors.stroke}`,
          p: 2,
          justifyContent: 'flex-start'
        }}
      >
        {onConfirm && (
          <Button onClick={handleConfirmClick} disabled={hasConfirmDisabled} variant='contained'>
            {confirmLabel}
          </Button>
        )}
        {onCancel && (
          <Button variant='outlined' size='medium' onClick={handleCancelClick}>
            {cancelLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default forwardRef(FdDialog)
